import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Empty, Table, TableColumnsType, TableProps } from 'antd'
import DropdownMenu from 'UI/DropdownMenu/DropdownMenu'
import styles from './VerticalTable.module.scss'
import { InsertRowRightOutlined } from '@ant-design/icons'
import cx from 'classnames'
import ResizableTitle from './ResizableTitle'
import type { ResizeCallbackData } from 'react-resizable'

type TableDataType = Record<string, unknown>

interface IVerticalTable extends TableProps<TableDataType> {
  pageSize: number
  total?: number
  containerRef?: React.RefObject<HTMLDivElement>
  additionalRefs?: React.RefObject<HTMLDivElement>[]
  disabledColumns?: string[] | null
  dataIsLoading?: boolean
  enableColumnPicker?: boolean
  removeFromPickerColumns?: string[] | null
  topBarContainerClassName?: string
  diffHeight?: number
  fetchNextPage?: () => void
  onSelectionChange?: (ids: string[]) => void
}

const VerticalTable: FC<IVerticalTable> = ({
  pageSize,
  columns,
  dataSource,
  children,
  containerRef,
  additionalRefs,
  dataIsLoading = false,
  total,
  disabledColumns = [],
  enableColumnPicker,
  removeFromPickerColumns,
  topBarContainerClassName,
  diffHeight,
  fetchNextPage,
  onSelectionChange,
  ...rest
}) => {
  const tableTopBarContainerRef = useRef<HTMLDivElement | null>(null)
  const tableRef = useRef<HTMLDivElement>(null)
  const isFetchingRef = useRef<boolean>(false)

  const [tableHeight, setTableHeight] = useState(0)
  const [page, setPage] = useState<number>(1)

  const [columnsState, setColumnsState] = useState<TableColumnsType<TableDataType>>(columns || [])

  const defaultCheckedColumnsList = useMemo(() => {
    const columnsKeys = columns?.map(column => column.key as string) || []

    return columnsKeys.filter(columnKey => !(disabledColumns || []).includes(columnKey))
  }, [columns, disabledColumns])

  const [checkedColumnsList, setCheckedColumnsList] = useState<string[]>(defaultCheckedColumnsList)

  useEffect(() => {
    if (columns) {
      setColumnsState(columns)
    }
  }, [columns])

  useEffect(() => {
    if (!rest.loading) {
      isFetchingRef.current = false
    }
  }, [rest.loading])

  const handleResize = useCallback(
    (index: number) =>
      (_: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
        const newColumns = [...columnsState]
        newColumns[index] = {
          ...newColumns[index],
          width: size.width,
        }
        setColumnsState(newColumns)
      },
    [columnsState],
  )

  const mergedColumns = useMemo(() => {
    return columnsState
      .map<TableColumnsType<TableDataType>[number]>((col, index) => ({
        ...col,
        hidden: !checkedColumnsList.includes(col.key as string),
        onHeaderCell: (column: TableColumnsType<TableDataType>[number]) => ({
          width: column.width,
          onResize: handleResize(index) as React.ReactEventHandler<Element>,
        }),
      }))
      .filter(col => !col.hidden)
  }, [columnsState, checkedColumnsList, handleResize])

  const options = useMemo(
    () =>
      columns
        ?.filter(column => !removeFromPickerColumns?.includes(column.key as string))
        ?.map((column, i) => ({ label: column.title as string, key: `${column.key || i}` })),
    [columns, removeFromPickerColumns],
  )

  useEffect(() => {
    const calculateHeight = () => {
      if (!containerRef?.current || !tableTopBarContainerRef.current || !tableRef.current || !additionalRefs) return

      const containerHeight = containerRef.current.clientHeight
      const tableTopBarContainerHeight = tableTopBarContainerRef.current.clientHeight

      const additionalHeight = additionalRefs.reduce((sum, ref) => {
        return sum + (ref.current ? ref.current.clientHeight : 0)
      }, 0)

      const tableHeader = tableRef.current.querySelector('.ant-table-thead') as HTMLTableSectionElement
      const tableHeaderHeight = tableHeader ? tableHeader.clientHeight : 0

      const newHeight =
        containerHeight - additionalHeight - tableTopBarContainerHeight - tableHeaderHeight - (diffHeight || 0)
      setTableHeight(newHeight > 0 ? newHeight : 0)
    }

    const observer = new ResizeObserver(calculateHeight)
    if (containerRef?.current) observer.observe(containerRef.current)
    additionalRefs?.forEach(ref => ref.current && observer.observe(ref.current))
    if (tableTopBarContainerRef.current) observer.observe(tableTopBarContainerRef.current)
    if (tableRef.current) observer.observe(tableRef.current)

    calculateHeight()

    return () => observer.disconnect()
  }, [containerRef, additionalRefs, diffHeight])

  const isEmptyTable = useMemo(() => (dataSource && dataSource.length === 0) || !dataSource, [dataSource])

  return (
    <>
      {(children || enableColumnPicker) && (
        <div ref={tableTopBarContainerRef} className={cx(styles.topBarContainer, topBarContainerClassName)}>
          {children}
          {enableColumnPicker && (
            <DropdownMenu items={options || []} checkedKeys={checkedColumnsList} setCheckedKeys={setCheckedColumnsList}>
              <InsertRowRightOutlined className={styles.verticalTableDropdownIcon} />
            </DropdownMenu>
          )}
        </div>
      )}
      <div ref={tableRef}>
        <Table
          {...rest}
          rowKey='id'
          rootClassName={cx(styles.rootClassName, isEmptyTable && styles.emptyTableRootClassName)}
          scroll={rest.scroll || { x: 'max-content', y: containerRef && additionalRefs ? tableHeight : 'auto' }}
          columns={mergedColumns}
          dataSource={isEmptyTable ? [{ id: 'empty' }] : dataSource}
          pagination={false}
          onScroll={(e: React.UIEvent<HTMLDivElement>) => {
            const target = e.currentTarget as HTMLElement

            if (
              target.scrollTop + target.clientHeight + 10 >= target.scrollHeight &&
              !dataIsLoading &&
              !isFetchingRef.current &&
              total &&
              page * pageSize < total
            ) {
              isFetchingRef.current = true
              setPage(prevPage => {
                fetchNextPage?.()
                return prevPage + 1
              })
            }
          }}
          rowSelection={
            onSelectionChange
              ? {
                  type: 'checkbox',
                  columnTitle: <></>,
                  onChange: (selectedRowKeys: React.Key[]) => {
                    onSelectionChange(selectedRowKeys as string[])
                  },
                }
              : undefined
          }
          footer={
            !isEmptyTable
              ? rest.footer
              : () => {
                  return (
                    <div className={styles.emptyTable}>
                      <Empty description='No data' image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                  )
                }
          }
          components={{
            header: {
              cell: ResizableTitle,
            },
          }}
        />
      </div>
    </>
  )
}

export default VerticalTable
